import { Client, Options, Hub } from "@jupiter/types";
import { Logger } from "@jupiter/utils";
import { getCurrentHub } from "@jupiter/hub";
import { EventBuilder } from "@jupiter/event";

/** A class object that can instantiate Client objects. */
export type ClientClass<F extends Client, O extends Options> = new (
  options: O
) => F;

/**
 * Internal function to create a new SDK client instance. The client is
 * installed and then bound to the current scope.
 *
 * @param clientClass The client class to instantiate.
 * @param options Options to pass to the client.
 */
export function initAndBind<F extends Client, O extends Options>(
  clientClass: ClientClass<F, O>,
  options: O
): void {
  if (options.debug === true) {
    Logger.enable();
  }

  const hub = getCurrentHub();
  const client = new clientClass(options);
  hub.bindClient(client);
}

/**
 * This calls a function on the current hub.
 * @param method function to call on hub.
 * @param args to pass to function.
 */
function _callOnHub<T>(method: string, ...args: any[]): T {
  const hub = getCurrentHub();
  if (hub && hub[method as keyof Hub]) {
    return (hub[method as keyof Hub] as any)(...args);
  }
  throw new Error(
    `No hub defined or ${method} was not found on the hub, please open a bug report.`
  );
}

export function track(
  eventName: string,
  otherProperties?: Record<string, any>
) {
  const event = EventBuilder.createCustomEvent(eventName, otherProperties);
  _callOnHub("track", event, otherProperties);
}

export function trackException(
  exception: any,
  otherProperties?: Record<string, any>
) {
  const event = EventBuilder.createExceptionEvent(exception, otherProperties);
  _callOnHub("track", event, otherProperties);
}
